import { utmParametersToKeysMapping } from 'public/assets/js/analytics/cookie';

const UTM_SOURCE_KEY = 'utm_source__c';
const UTM_MEDIUM_KEY = 'utm_medium__c';
const GCLID_KEY = 'gclid__c';
const MICROSOFT_LEAD_ID_KEY = 'Microsoft_Lead_ID__c';

/**
 * Marketo Original Source for unpaid search-engine referrers uses
 * `utm_medium=search` and the engine name as `utm_source` (e.g. google).
 * Match the search host itself (`google.com`, `search.yahoo.com`), not product
 * hosts such as `mail.google.com`. Paid click hosts such as
 * googleadservices.com stay unmatched.
 */
const SEARCH_ENGINE_SOURCES: ReadonlyArray<{
  source: string;
  isMatch: (hostname: string) => boolean;
}> = [
  {
    source: 'google',
    isMatch: (hostname) => {
      return /^google\.(?:com|co\.[a-z]{2}|com\.[a-z]{2}|[a-z]{2})$/i.test(
        hostname,
      );
    },
  },
  {
    source: 'bing',
    isMatch: (hostname) => {
      return /^(?:[a-z]{2}\.)?bing\.com$/i.test(hostname);
    },
  },
  {
    source: 'yahoo',
    isMatch: (hostname) => {
      return /^(?:(?:[a-z]{2}\.)?(?:r\.)?search\.)?yahoo\.(?:com|co\.[a-z]{2}|com\.[a-z]{2}|[a-z]{2})$/i.test(
        hostname,
      );
    },
  },
  {
    source: 'duckduckgo',
    isMatch: (hostname) => {
      return /(?:^|\.)duckduckgo\.com$/i.test(hostname);
    },
  },
  {
    source: 'baidu',
    isMatch: (hostname) => {
      return /(?:^|\.)baidu\.com$/i.test(hostname);
    },
  },
  {
    source: 'yandex',
    isMatch: (hostname) => {
      return /(?:^|\.)yandex\.(?:com|ru|[a-z]{2})$/i.test(hostname);
    },
  },
  {
    source: 'naver',
    isMatch: (hostname) => {
      return /(?:^|\.)naver\.com$/i.test(hostname);
    },
  },
  {
    source: 'ecosia',
    isMatch: (hostname) => {
      return /(?:^|\.)ecosia\.org$/i.test(hostname);
    },
  },
  {
    source: 'brave',
    isMatch: (hostname) => {
      return /(?:^|\.)search\.brave\.com$/i.test(hostname);
    },
  },
];

const asNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const isFilled = (value: unknown): boolean => {
  return asNonEmptyString(value) != null;
};

const parseAbsoluteUrl = (value: unknown): URL | null => {
  const raw = asNonEmptyString(value);
  if (raw == null) {
    return null;
  }

  try {
    return new URL(raw);
  } catch {
    return null;
  }
};

const fillMissingUtmsFromUrl = (
  fields: Record<string, unknown>,
  url: URL | null,
): void => {
  if (url == null) {
    return;
  }

  Object.entries(utmParametersToKeysMapping).forEach(
    ([marketoKey, queryKey]) => {
      if (isFilled(fields[marketoKey])) {
        return;
      }
      const fromQuery = asNonEmptyString(url.searchParams.get(queryKey) ?? '');
      if (fromQuery != null) {
        fields[marketoKey] = fromQuery;
      }
    },
  );
};

const inferSearchEngineFromReferrer = (
  fields: Record<string, unknown>,
): void => {
  if (
    isFilled(fields[UTM_SOURCE_KEY]) ||
    isFilled(fields[UTM_MEDIUM_KEY]) ||
    isFilled(fields[GCLID_KEY]) ||
    isFilled(fields[MICROSOFT_LEAD_ID_KEY])
  ) {
    return;
  }

  const referrer =
    parseAbsoluteUrl(fields.referrerUrl) ??
    parseAbsoluteUrl(fields.referralURL);
  if (referrer == null) {
    return;
  }

  const hostname = referrer.hostname.replace(/^www\./i, '');
  const engine = SEARCH_ENGINE_SOURCES.find(({ isMatch }) => {
    return isMatch(hostname);
  });
  if (engine == null) {
    return;
  }

  fields[UTM_SOURCE_KEY] = engine.source;
  fields[UTM_MEDIUM_KEY] = 'search';
};

/**
 * Prefer already-filled keys on `base`. Overlay only supplies values Marketo
 * already had on the native form (autofill) that our `valObj` omitted.
 */
export const mergeFilledMarketoVals = (
  base: Record<string, unknown>,
  overlay: unknown,
): Record<string, unknown> => {
  const merged: Record<string, unknown> = { ...base };
  if (
    overlay == null ||
    typeof overlay !== 'object' ||
    Array.isArray(overlay)
  ) {
    return merged;
  }

  Object.entries(overlay as Record<string, unknown>).forEach(([key, value]) => {
    if (!isFilled(merged[key]) && isFilled(value)) {
      merged[key] = value;
    }
  });

  return merged;
};

/**
 * Fill UTM keys Conversion would otherwise drop, using the same signals
 * Marketo uses: query params on the current/entry URL, then unpaid
 * search-engine referrer inference (`google` / `search`). Never overwrites a
 * filled UTM, and skips referrer inference when `gclid__c` or
 * `Microsoft_Lead_ID__c` is set.
 */
export const inferMarketoUtmFields = (
  valObj: Record<string, unknown>,
): Record<string, unknown> => {
  const fields: Record<string, unknown> = { ...valObj };

  fillMissingUtmsFromUrl(fields, parseAbsoluteUrl(fields.currentURL));
  fillMissingUtmsFromUrl(fields, parseAbsoluteUrl(fields.entryURL));
  inferSearchEngineFromReferrer(fields);

  return fields;
};
